/**
 * Labstep
 *
 * @module components/Experiment/List
 * @desc Experiment List
 */

import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { EntityDraggable } from 'labstep-web/components/Entity/Draggable';
import { ExperimentCard } from 'labstep-web/components/Experiment/Card';
import { EntityUpdateAnyContainer } from 'labstep-web/containers/Entity/Update/Any';
import { Icon } from 'labstep-web/core/Icon';
import { List } from 'labstep-web/core/List';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { PositionService } from 'labstep-web/services/position.service';
import type React from 'react';
import { useState } from 'react';
import type { ExperimentListProps } from './types';

export const ExperimentList: React.FC<ExperimentListProps> = ({
  experiments,
  experimentWorkflowId,
}) => {
  const canEdit = useHasAccess(
    ExperimentWorkflow.entityName,
    experimentWorkflowId,
    Action.edit,
  );
  return (
    <EntityUpdateAnyContainer entityName={Experiment.entityName}>
      {({ update }) => (
        <DragDropContext
          onDragEnd={(result: DropResult) => {
            PositionService.onDragEnd(
              result,
              experiments,
              update as any,
            );
          }}
        >
          <Droppable
            droppableId="experiment-list"
            isDropDisabled={!canEdit}
          >
            {(provided) => (
              <div id="experiment-list" ref={provided.innerRef}>
                <List>
                  {experiments
                    .sort((a, b) => a.position - b.position)
                    .map((experiment, index) => (
                      <Draggable
                        key={`experiment-${experiment.id}`}
                        draggableId={`experiment:${experiment.guid}`}
                        index={index}
                      >
                        {(draggableProvided) => {
                          return (
                            <EntityDraggable
                              entityName="experiment"
                              entityId={experiment.id}
                              entityGuid={experiment.guid}
                            >
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                              >
                                <ExperimentCard
                                  dragHandle={
                                    <Icon
                                      name="sort"
                                      popup={{
                                        content: 'Drag to reorder',
                                      }}
                                      {...draggableProvided.dragHandleProps}
                                    />
                                  }
                                  experiment={experiment}
                                />
                              </div>
                            </EntityDraggable>
                          );
                        }}
                      </Draggable>
                    ))}
                </List>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </EntityUpdateAnyContainer>
  );
};
